/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, useEffect, useMemo } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useServices } from "@/services/hooks/useServices";
import { toast } from "sonner";
import { AuthProvider } from "@/types/common";

export function useEmailGeneration() {
  const [testEmail, setTestEmail] = useState<string | null>(null);
  const { healthTestService } = useServices();

  const { mutate: generateNewTestEmail, isPending: isGenerating } = useMutation(
    {
      mutationFn: async () => {
        const randomAlias = healthTestService.generateRandomAlias();
        return healthTestService.createNewAlias(randomAlias);
      },
      onSuccess: (newAlias) => {
        setTestEmail(newAlias.alias);
        toast.success("New test email generated successfully!");
      },
      onError: () => {
        toast.error("Failed to generate test email");
      },
    }
  );

  const reset = () => {
    setTestEmail(null);
  };

  return {
    testEmail,
    isGenerating,
    generateNewTestEmail,
    reset,
  };
}
export function useBulkEmailGeneration() {
  const { healthTestService } = useServices();

  return useMutation({
    mutationFn: async (numberOfAliases: number) => {
      return healthTestService.generateBulkAliases(numberOfAliases);
    },
    onSuccess: () => {
      toast.success("New test emails generated successfully!");
    },
    onError: () => {
      toast.error("Failed to generate test email");
    },
  });
}

export function useEmailResults(
  testEmail: string | null,
  hasMailSent: boolean
) {
  const { healthTestService } = useServices();

  const {
    data: healthResults,
    isLoading: isLoadingResults,
    error: resultsError,
  } = useQuery({
    queryKey: ["emailHealthResults", testEmail],
    queryFn: () =>
      testEmail ? healthTestService.getEmailHealthResults(testEmail) : null,
    enabled: !!testEmail && hasMailSent,
    refetchInterval: (data) => (data ? false : 5000), // 5 seconds
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    retry: true,
    staleTime: 0,
  });

  return {
    healthResults,
    isLoadingResults,
    hasError: !!resultsError,
  };
}

export function useEmailHealthHistory(
  page: number = 1,
  limit: number = 10,
  from: Date,
  to: Date,
  domain: string
) {
  const { healthTestService } = useServices();

  const filters = useMemo(() => {
    return {
      page,
      limit,
      from: from.toISOString(),
      to: to.toISOString(),
      domain,
    };
  }, [page, limit, from, to, domain]);

  return useQuery({
    queryKey: ["emailHealthHistory", filters],
    queryFn: () => healthTestService.getCompanyEmailResults(filters),
    placeholderData: keepPreviousData,
  });
}

export function useBulkEmailTest() {
  const { healthTestService } = useServices();

  const { mutateAsync: runBulkEmailTest, isPending: isRunningBulkTest } =
    useMutation({
      mutationFn: async (domains: string[]) => {
        const results = await Promise.all(
          domains.map(async (domain) => {
            const randomAlias = healthTestService.generateRandomAlias();
            const newAlias = await healthTestService.createNewAlias(
              randomAlias
            );
            return { domain, testEmail: newAlias.alias };
          })
        );
        return results;
      },
      onSuccess: () => {
        toast.success("Bulk email test initiated successfully!");
      },
      onError: () => {
        toast.error("Failed to initiate bulk email test");
      },
    });

  return {
    runBulkEmailTest,
    isRunningBulkTest,
  };
}

export function useScheduledEmailTestMutations(
  type: "placement" | "email-health"
) {
  const { healthTestService } = useServices();
  const queryClient = useQueryClient();

  const { mutateAsync: createScheduledTest, isPending: isCreating } =
    useMutation({
      mutationFn: async ({
        domains,
        scheduleType,
        recurrenceType,
        startTime,
        customRecurrence,
        espType,
      }: {
        domains: string[];
        scheduleType: "once" | "recurring";
        recurrenceType: string;
        startTime: Date;
        espType: AuthProvider;
        customRecurrence?: {
          interval: number;
          unit: string;
          endType: string;
          endDate?: Date;
          occurrences?: number;
          weekdays?: number[];
        };
      }) => {
        return healthTestService.createScheduledTest(domains, type, espType, {
          type: scheduleType,
          recurrenceType,
          startTime,
          customRecurrence,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["scheduledEmailTests", type],
        });
        toast.success("Test scheduled successfully!");
      },
      onError: () => {
        toast.error("Failed to schedule test");
      },
    });

  const { mutateAsync: deleteScheduledTest, isPending: isDeleting } =
    useMutation({
      mutationFn: (id: string) => healthTestService.deleteScheduledTest(id),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["scheduledEmailTests", type],
        });
        toast.success("Test deleted successfully!");
      },
      onError: () => {
        toast.error("Failed to delete test");
      },
    });

  const { mutateAsync: updateScheduledTest, isPending: isUpdating } =
    useMutation({
      mutationFn: ({
        id,
        domains,
        scheduleType,
        recurrenceType,
        startTime,
        customRecurrence,
      }: {
        id: string;
        domains: string[];
        scheduleType: "once" | "recurring";
        recurrenceType: string;
        startTime: Date;
        customRecurrence?: {
          interval: number;
          unit: string;
          endType: string;
          endDate?: Date;
          occurrences?: number;
          weekdays?: number[];
        };
      }) => {
        return healthTestService.updateScheduledTest(id, domains, {
          type: scheduleType,
          recurrenceType,
          startTime,
          customRecurrence,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["scheduledEmailTests", type],
        });
        toast.success("Test updated successfully!");
      },
      onError: () => {
        toast.error("Failed to update test");
      },
    });

  return {
    createScheduledTest,
    isCreating,
    deleteScheduledTest,
    isDeleting,
    updateScheduledTest,
    isUpdating,
  };
}

export const useScheduledEmailTests = (type: "placement" | "email-health") => {
  const { healthTestService } = useServices();
  return useQuery({
    queryKey: ["scheduledEmailTests", type],
    queryFn: () => healthTestService.getScheduledTests(type),
  });
};

export function usePlacementEmailTests() {
  const { healthTestService } = useServices();

  return useQuery({
    queryKey: ["placementEmailTests"],
    queryFn: () => healthTestService.getPlacementEmailTests(),
  });
}

export function useEmailHealthExport() {
  const { healthTestService } = useServices();

  const { mutate: exportResults, isPending: isExporting } = useMutation({
    mutationFn: async ({
      from,
      to,
      domain,
    }: {
      from: Date;
      to: Date;
      domain: string;
    }) => {
      const response = await healthTestService.exportEmailResults(
        from,
        to,
        domain
      );

      // Create a blob from the CSV data
      const blob = new Blob([response as unknown as BlobPart], {
        type: "text/csv",
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `email-health-report-${new Date().toISOString()}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      window.cioanalytics?.track("email_health_test_history_exported", {
        domain: domain,
      });
    },
    onError: () => {
      toast.error("Failed to export results");
    },
  });

  return {
    exportResults,
    isExporting,
  };
}

export function useDeliveryStatus(domain: string, isEnabled = false) {
  const { healthTestService } = useServices();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<string | undefined>(undefined);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [errorSteps, setErrorSteps] = useState<string[]>([]);
  const [warningSteps, setWarningSteps] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateTimeRef = useRef<number>(Date.now());

  // Map backend stages to UI steps
  const stageToStepMap: Record<string, string> = {
    started: "email_auth",
    checking_dns_records: "dns",
    checking_mx_records: "dns",
    checking_mail_server_security: "mail_server",
    checking_mail_servers: "mail_server",
    checking_blacklists: "blacklist",
    saving_results: "rpki",
  };

  // Function to update the UI based on status data
  const updateUIFromStatus = (data: any) => {
    if (!data.processing) {
      // Test is complete
      setIsLoading(false);
      setIsComplete(true);
      return true; // Return true to indicate completion
    }

    // Update last update time
    lastUpdateTimeRef.current = Date.now();

    // Map the backend stage to a UI step
    const step = stageToStepMap[data.stage] || data.stage;

    // Update current step
    setCurrentStep(step);

    // Mark previous steps as completed
    const allSteps = Object.keys(stageToStepMap);
    const currentStepIndex = allSteps.indexOf(step);

    if (currentStepIndex > 0) {
      const previousSteps = allSteps
        .slice(0, currentStepIndex)
        .map((key) => stageToStepMap[key]);
      setCompletedSteps(previousSteps);
    }

    // Handle errors if they exist in the data
    if (data.errors && Array.isArray(data.errors)) {
      const errorTypes = data.errors.map((err: any) => err.type);
      setErrorSteps(errorTypes);
    }

    // Handle warnings if they exist in the data
    if (data.warnings && Array.isArray(data.warnings)) {
      const warningTypes = data.warnings.map((warn: any) => warn.type);
      setWarningSteps(warningTypes);
    }

    return false; // Return false to indicate not complete
  };

  // Function to check status via HTTP
  const checkStatus = async () => {
    try {
      const status = await healthTestService.getDeliveryStatus(domain);

      if (status.processing) {
        setIsLoading(true);

        // Update UI based on status
        updateUIFromStatus(status);

        // Start listening for real-time updates
        startEventStream();

        // Start polling as a backup
        startPolling();
      } else {
        setIsLoading(false);
        setIsComplete(true);
        stopPolling();
      }
    } catch (error) {
      console.error("Error checking delivery status:", error);
      // Don't set isLoading to false here - we'll keep trying with the polling
    }
  };

  // Function to start SSE event stream
  const startEventStream = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const eventSource = healthTestService.createDeliveryStatusStream(domain);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const isCompleted = updateUIFromStatus(data);

          if (isCompleted) {
            eventSource.close();
            eventSourceRef.current = null;
            stopPolling();
          }
        } catch (error) {
          console.error("Error parsing event data:", error);
        }
      };

      eventSource.onerror = () => {
        console.error("EventSource error - falling back to polling");
        eventSource.close();
        eventSourceRef.current = null;
        // We'll rely on polling as a fallback
      };
    } catch (error) {
      console.error("Error creating event source:", error);
      // We'll rely on polling as a fallback
    }
  };

  // Function to start polling
  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        // Check if we haven't received updates for a while (30 seconds)
        const timeSinceLastUpdate = Date.now() - lastUpdateTimeRef.current;

        // Only make a new request if we haven't received updates recently
        if (timeSinceLastUpdate > 10000) {
          // 10 seconds threshold
          console.log("Polling for status updates...");
          const status = await healthTestService.getDeliveryStatus(domain);
          const isCompleted = updateUIFromStatus(status);

          if (isCompleted || !status.processing) {
            stopPolling();
          }
        }
      } catch (error) {
        console.error("Error polling for status:", error);
      }
    }, 30000); // Poll every 30 seconds
  };

  // Function to stop polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!isEnabled || !domain) {
      // Reset state when disabled
      setIsLoading(false);
      setCurrentStep(undefined);
      setCompletedSteps([]);
      setErrorSteps([]);
      setWarningSteps([]);
      setIsComplete(false);
      stopPolling();

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      return;
    }

    // Initialize last update time
    lastUpdateTimeRef.current = Date.now();

    // Start the initial check
    checkStatus();

    // Cleanup function
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      stopPolling();
    };
  }, [domain, isEnabled]);

  return {
    isLoading,
    currentStep,
    completedSteps,
    errorSteps,
    warningSteps,
    isComplete,
  };
}
